/**
 * Thin wrappers over the two modular Cognito SDK clients (no Amplify).
 *
 * All operations here are Cognito's UNSIGNED public API (GetId /
 * GetCredentialsForIdentity for the Identity Pool; SignUp / ConfirmSignUp /
 * InitiateAuth / RespondToAuthChallenge for the User Pool) — none require
 * ambient AWS credentials, so the clients are constructed with region only.
 *
 * This is the real `CognitoAdapter` (see session.ts). Unit tests inject a fake
 * adapter instead of touching AWS; correctness of the real path is proven by
 * the live e2e.
 */
import {
  CognitoIdentityClient,
  GetCredentialsForIdentityCommand,
  GetIdCommand,
} from '@aws-sdk/client-cognito-identity';
import {
  CognitoIdentityProviderClient,
  ConfirmSignUpCommand,
  InitiateAuthCommand,
  RespondToAuthChallengeCommand,
  SignUpCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { authConfig, userPoolLoginKey, type AuthConfig } from './config';
import { SrpClient } from './srp';
import type { AuthTokens, AwsCredentials } from './types';

let identityClient: CognitoIdentityClient | undefined;
let providerClient: CognitoIdentityProviderClient | undefined;

function identity(config: AuthConfig): CognitoIdentityClient {
  identityClient ??= new CognitoIdentityClient({ region: config.region });
  return identityClient;
}

function provider(config: AuthConfig): CognitoIdentityProviderClient {
  providerClient ??= new CognitoIdentityProviderClient({ region: config.region });
  return providerClient;
}

/** GetId with no logins → a fresh guest IdentityId (the uid). */
export async function getGuestIdentityId(
  config: AuthConfig = authConfig,
): Promise<string> {
  const out = await identity(config).send(
    new GetIdCommand({ IdentityPoolId: config.identityPoolId }),
  );
  if (!out.IdentityId) throw new Error('GetId returned no IdentityId');
  return out.IdentityId;
}

/**
 * GetId with a User Pool id-token login → the IdentityId Cognito already has
 * on file for that login (minting one only if none exists yet). Used as the
 * fallback when `getCredentialsForIdentity` on the cached guest id rejects
 * with a conflict because the login is already linked to a DIFFERENT
 * identity (a returning Google user on a new device, or cleared storage).
 */
export async function resolveIdentityForLogin(
  idToken: string,
  config: AuthConfig = authConfig,
): Promise<string> {
  const out = await identity(config).send(
    new GetIdCommand({
      IdentityPoolId: config.identityPoolId,
      Logins: { [userPoolLoginKey(config)]: idToken },
    }),
  );
  if (!out.IdentityId) throw new Error('GetId (with login) returned no IdentityId');
  return out.IdentityId;
}

/**
 * GetCredentialsForIdentity on a KNOWN IdentityId. With no logins → guest
 * credentials. With a User Pool id-token login → authed credentials linked
 * onto that same IdentityId (the uid is unchanged).
 */
export async function getCredentialsForIdentity(
  identityId: string,
  logins?: Record<string, string>,
  config: AuthConfig = authConfig,
): Promise<AwsCredentials> {
  const out = await identity(config).send(
    new GetCredentialsForIdentityCommand({ IdentityId: identityId, Logins: logins }),
  );
  const c = out.Credentials;
  if (!c?.AccessKeyId || !c.SecretKey || !c.SessionToken) {
    throw new Error('GetCredentialsForIdentity returned incomplete credentials');
  }
  return {
    accessKeyId: c.AccessKeyId,
    secretAccessKey: c.SecretKey,
    sessionToken: c.SessionToken,
    expiresAt: c.Expiration ? c.Expiration.getTime() : undefined,
  };
}

/** SignUp (email as username). Throws UsernameExistsException etc. verbatim. */
/**
 * A fresh ID token from the stored refresh token.
 *
 * Cognito ID tokens last an hour. Without this, a learner who trained past that point got a 401 on
 * their next answer and the app had nothing to do about it but show the status code — which is what
 * the founder ran into mid-session (2026-08-26). REFRESH_TOKEN_AUTH needs no user interaction and no
 * secret, so the recovery is silent.
 *
 * Returns undefined when the refresh token itself has expired or been revoked. That is the one case
 * where the learner genuinely has to sign in again, and the caller says so rather than guessing.
 */
export async function refreshIdToken(
  refreshToken: string,
  config: AuthConfig = authConfig,
): Promise<string | undefined> {
  try {
    const result = await new CognitoIdentityProviderClient({ region: config.region }).send(
      new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: config.userPoolClientId,
        AuthParameters: { REFRESH_TOKEN: refreshToken },
      }),
    );
    return result.AuthenticationResult?.IdToken;
  } catch {
    return undefined;
  }
}

export async function signUp(
  email: string,
  password: string,
  config: AuthConfig = authConfig,
): Promise<void> {
  await provider(config).send(
    new SignUpCommand({
      ClientId: config.userPoolClientId,
      Username: email,
      Password: password,
      UserAttributes: [{ Name: 'email', Value: email }],
    }),
  );
}

/** ConfirmSignUp with the emailed code. */
export async function confirmSignUp(
  email: string,
  code: string,
  config: AuthConfig = authConfig,
): Promise<void> {
  await provider(config).send(
    new ConfirmSignUpCommand({
      ClientId: config.userPoolClientId,
      Username: email,
      ConfirmationCode: code,
    }),
  );
}

/**
 * USER_SRP_AUTH sign-in. Two round-trips: InitiateAuth (send SRP_A) →
 * PASSWORD_VERIFIER challenge → RespondToAuthChallenge (answer with the SRP
 * signature). Returns the User Pool tokens; the ID token carries the `email`
 * claim the backend reads.
 */
export async function srpSignIn(
  email: string,
  password: string,
  config: AuthConfig = authConfig,
): Promise<AuthTokens> {
  const client = provider(config);
  const srp = new SrpClient();

  const init = await client.send(
    new InitiateAuthCommand({
      AuthFlow: 'USER_SRP_AUTH',
      ClientId: config.userPoolClientId,
      AuthParameters: { USERNAME: email, SRP_A: srp.srpA },
    }),
  );
  if (init.ChallengeName !== 'PASSWORD_VERIFIER' || !init.ChallengeParameters) {
    throw new Error(`Unexpected SRP challenge: ${init.ChallengeName ?? 'none'}`);
  }
  const cp = init.ChallengeParameters;

  const { timestamp, signature } = await srp.sign({
    userIdForSrp: cp.USER_ID_FOR_SRP!,
    password,
    saltHex: cp.SALT!,
    srpBHex: cp.SRP_B!,
    secretBlock: cp.SECRET_BLOCK!,
    poolName: config.userPoolId.split('_')[1]!,
  });

  const resp = await client.send(
    new RespondToAuthChallengeCommand({
      ClientId: config.userPoolClientId,
      ChallengeName: 'PASSWORD_VERIFIER',
      Session: init.Session,
      ChallengeResponses: {
        USERNAME: cp.USER_ID_FOR_SRP!,
        PASSWORD_CLAIM_SECRET_BLOCK: cp.SECRET_BLOCK!,
        PASSWORD_CLAIM_SIGNATURE: signature,
        TIMESTAMP: timestamp,
      },
    }),
  );
  const result = resp.AuthenticationResult;
  if (!result?.IdToken || !result.AccessToken) {
    throw new Error('SRP sign-in returned no tokens');
  }
  return {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    refreshToken: result.RefreshToken,
    expiresAt: result.ExpiresIn ? Date.now() + result.ExpiresIn * 1000 : undefined,
  };
}

/** Provider-key helper re-exported for the link step. */
export { userPoolLoginKey };
